import type { ConsoleTab } from '@badger-sentinel/rules';

import { tabLabel } from '../model/tabs.js';

/**
 * The tab strip (design 149–156).
 *
 * Driven by `capabilities.tabs`, which Phase 0.5 put on the wire precisely so
 * this strip is correct on first paint rather than after a snapshot. The order
 * is canonical (`CONSOLE_TABS`) and the projection guarantees it, so nothing
 * here sorts.
 *
 * A seat with one tab still gets a strip. Hiding it would make the console two
 * different shapes depending on the seat, and the tab strip is where a player
 * looks to learn what their console *can* show them.
 *
 * ## The attention dot, finally
 *
 * Phase 1 rendered none: nothing could raise it, and "a marker that never lights
 * is worse than no marker, because the first time it does light nobody will
 * believe it". Phase 4 gives it its source — an `mw.stage` arrival on a tab the
 * operator is not looking at. The rule that keeps it believable is that the shell
 * raises it only on a *new* arrival and clears it on tab entry and on
 * `phase.changed`, so a lit dot always means "something landed here that you have
 * not seen".
 *
 * The strip itself stays dumb: it is handed the set of tabs wanting attention and
 * draws one dot each. It has no idea what missile warning is, which is what will
 * let Phase 8's intel arrivals use it without touching this file.
 */

export interface TabStripProps {
  readonly tabs: readonly ConsoleTab[];
  readonly active: ConsoleTab | null;
  readonly onSelect: (tab: ConsoleTab) => void;
  /** Tabs with something unseen on them. The active tab never has one. */
  readonly attention?: readonly ConsoleTab[];
}

export function TabStrip(props: TabStripProps): JSX.Element {
  return (
    <div
      data-mat="chrome"
      data-testid="tab-strip"
      style={{
        flex: 'none',
        height: 32,
        display: 'flex',
        alignItems: 'stretch',
        padding: '0 12px',
        overflow: 'hidden',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {/*
        `role="tablist"` and `aria-selected`, not `aria-current="page"`.
        These select the module in the centre pane; they are not page links, and
        the previous markup announced them as navigation to a different document.
      */}
      <div
        role="tablist"
        aria-label="Console modules"
        style={{
          flex: '0 1 auto',
          minWidth: 0,
          display: 'flex',
          alignItems: 'stretch',
          gap: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        {props.tabs.map((tab) => {
          const active = tab === props.active;
          // A dot on the tab you are already on is a marker for something in front
          // of you, which is the reading that makes people stop trusting markers.
          const wants = !active && (props.attention ?? []).includes(tab);
          return (
            <button
              key={tab}
              type="button"
              data-testid={`tab-${tab}`}
              data-attention={wants ? 'true' : undefined}
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => props.onSelect(tab)}
              onKeyDown={(event) => {
                /*
                 * The other half of a roving tabindex.
                 *
                 * `tabIndex={-1}` on the inactive tabs takes them out of the Tab
                 * ring on purpose — a tablist is one stop, not one per tab — and
                 * the arrows are what you get instead. Without them the strip
                 * announced itself as a tablist and then had no keyboard route
                 * to any tab but the one already open, which is worse than
                 * plain buttons would have been.
                 *
                 * Home and End because a seat with eight tabs is a long walk,
                 * and both are part of the pattern every screen reader's docs
                 * describe.
                 */
                const at = props.tabs.indexOf(tab);
                const to =
                  event.key === 'ArrowRight'
                    ? at + 1
                    : event.key === 'ArrowLeft'
                      ? at - 1
                      : event.key === 'Home'
                        ? 0
                        : event.key === 'End'
                          ? props.tabs.length - 1
                          : null;
                if (to === null) return;
                const next = props.tabs[Math.min(props.tabs.length - 1, Math.max(0, to))];
                if (next === undefined || next === tab) return;
                event.preventDefault();
                props.onSelect(next);
                // Selection follows focus here, so the newly active tab is the
                // one that now holds the ring — and it has to be told to take it.
                requestAnimationFrame(() => {
                  const node = document.querySelector<HTMLElement>(
                    '[data-testid="tab-' + next + '"]',
                  );
                  node?.focus();
                });
              }}
              style={{
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '0 14px',
                background: active ? 'var(--surface-3)' : 'transparent',
                border: 0,
                borderBottom: `2px solid ${active ? 'var(--primary)' : 'transparent'}`,
                color: active ? 'var(--text)' : 'var(--text-muted)',
                fontFamily: 'var(--font-ui)',
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '.14em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {tabLabel(tab)}
              {wants && (
                <span
                  data-testid={`tab-dot-${tab}`}
                  aria-label="NEW DATA"
                  style={{
                    flex: 'none',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: 'var(--primary)',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
      <div style={{ flex: 1, minWidth: 8 }} />
    </div>
  );
}
